import {
    ManufacturerProductPropertyDetail,
    ManufacturerProductPropertyOptionDetail,
    ManufacturerProductPropertyOptionGroupDetail,
} from '@deltek/specification-client/core/Models/product'
import { getNormalizedProperties } from '@deltek/specification-client/core/Widgets/ProductListing/Details/productDetailUtils'

import { PropertyType, SupportedOptionsObject } from '../../ListingDetailsPage'
import { isMasterSpecPropertyGroup } from '../propertiesUtils'
import { NonMSPropertyType } from './NonMasterSpec/nonMSTypes'

export const composePropertiesInput = (
    normalizedProperties: Record<string, PropertyType>,
    pPlusItems: Record<
        string,
        Record<
            string,
            Record<
                string,
                Array<Partial<ManufacturerProductPropertyOptionDetail>>
            >
        >
    >,
    nonMSProperties: Array<NonMSPropertyType>
) => {
    const nonMSProps = [
        ...nonMSProperties.filter((prop) => !prop.propertyName),
        ...nonMSProperties.filter((prop) => !!prop.propertyName),
    ].reduce((acc: Record<string, PropertyType>, curr) => {
        acc[curr.propertyName] = curr.subProperties.reduce(
            (
                subPropArray: Array<Partial<ManufacturerProductPropertyDetail>>,
                { optionGroups, propertyId, subPropertyName }
            ) => {
                subPropArray.push({
                    isPremiumPlus: true,
                    options: [],
                    optionGroups,
                    propertyId,
                    propertyName: curr.propertyName,
                    subPropertyName,
                    originalPropertyName: curr.propertyName,
                })
                return subPropArray
            },
            []
        )

        return acc
    }, {})

    const msProps = Object.entries(normalizedProperties)
        .filter(([_, property]) => isMasterSpecPropertyGroup(property))
        .reduce(
            (acc: Record<string, PropertyType>, [propertyName, property]) => {
                acc[propertyName] = [
                    ...(property
                        .filter((subProperty) => !subProperty.isPremiumPlus)
                        .map(
                            (
                                subProperty: Partial<
                                    ManufacturerProductPropertyDetail
                                >
                            ) => ({
                                ...subProperty,
                                optionGroups: subProperty.optionGroups?.map(
                                    (optGrp, optGrpIndex) => ({
                                        ...optGrp,
                                        options: [
                                            ...(((subProperty.optionGroups ??
                                                [])[
                                                optGrpIndex
                                            ] as ManufacturerProductPropertyOptionGroupDetail).options?.filter(
                                                (option) =>
                                                    !option.isPremiumPlus
                                            ) ?? []),
                                            ...(pPlusItems[propertyName][
                                                subProperty.subPropertyName ??
                                                    ''
                                            ][optGrp.groupId ?? ''] ?? []),
                                        ],
                                    })
                                ),
                            })
                        ) ?? []),
                    ...(nonMSProps[propertyName] ?? []),
                ]

                return acc
            },
            {}
        )

    const filteredNonMSProperties = Object.entries(nonMSProps)
        .filter(([key]) => !msProps[key])
        .reduce(
            (acc: Record<string, PropertyType>, [propertyName, property]) => {
                acc[propertyName] = property
                return acc
            },
            {}
        )

    return { ...msProps, ...filteredNonMSProperties }
}

export const composeSelectedOptions = (
    msSelectedOptions: SupportedOptionsObject,
    nonMSProperties: Array<NonMSPropertyType>
) => {
    const nonMSSupportedProps = nonMSProperties.reduce(
        (acc: Record<string, Record<string, Array<string>>>, curr) => {
            acc[curr.propertyName] = curr.subProperties.reduce(
                (
                    subPropObject: Record<string, Array<string>>,
                    { supportedOptionIds, subPropertyName }
                ) => {
                    subPropObject[subPropertyName] = supportedOptionIds
                    return subPropObject
                },
                {}
            )
            return acc
        },
        {}
    )

    const msSupportedProps = Object.entries(msSelectedOptions).reduce(
        (
            acc: Record<string, Record<string, Array<string>>>,
            [propertyName, supportedSubproperties]
        ) => {
            acc[propertyName] = {
                ...supportedSubproperties,
                ...(nonMSSupportedProps[propertyName] ?? {}),
            }
            return acc
        },
        {}
    )

    const filteredNonMSSupportedProperties = Object.entries(nonMSSupportedProps)
        .filter(([key]) => !msSelectedOptions[key])
        .reduce(
            (
                acc: Record<string, Record<string, Array<string>>>,
                [propertyName, property]
            ) => {
                acc[propertyName] = property
                return acc
            },
            {}
        )

    return { ...msSupportedProps, ...filteredNonMSSupportedProperties }
}

export const checkAllDeleteableOptions = (property?: NonMSPropertyType) => {
    // TODO: Junar implement this when deleteable is implemented
    return true
}

export const getSelectedProperties = (
    past?: Partial<ManufacturerProductPropertyDetail>[]
) => {
    const properties = getNormalizedProperties(past)

    return Object.entries(properties).reduce(
        (propAcc, [propertyName, property]) => {
            propAcc[propertyName] = property.reduce(
                (subPropAcc, subProp, subPropIndex) => {
                    subPropAcc[
                        subProp.subPropertyName ?? subPropIndex
                    ] = subProp.optionGroups?.reduce(
                        (
                            supportedOpts: string[],
                            optGrp: ManufacturerProductPropertyOptionGroupDetail
                        ) => {
                            if (optGrp.groupId && optGrp.options.length > 0) {
                                optGrp.options.forEach((option) => {
                                    if (
                                        !!option.isSupported &&
                                        !!option.optionId
                                    ) {
                                        supportedOpts.push(option.optionId)
                                    }
                                })
                            }
                            return supportedOpts
                        },
                        []
                    )
                    return subPropAcc
                },
                {}
            )
            return propAcc
        },
        {} as Record<string, Record<string, string[]>>
    )
}
